import { TemporaryProblemError } from '@orus.eu/pharaoh'

import { getPostHogInstance } from './posthog-integration.ts'
import { createTrackingMessage, sendMessage } from './tracking/tracking.ts'

type TallyPopupOptions = {
  width?: number
  layout?: 'default' | 'modal'
  hiddenFields?: {
    [key: string]: string | number | boolean | undefined | null
  }
  onSubmit?: (payload: { [key: string]: string | undefined | null }) => void
}

type TallyApi = { openPopup: (formId: string, options?: TallyPopupOptions) => void }

declare global {
  var Tally: TallyApi | undefined
}

const tallyEmbedScriptUrl = 'https://tally.so/widgets/embed.js'

// A stalled download fires neither load nor error.
const tallyLoadTimeoutMs = 10_000

let tallyLoading: Promise<void> | undefined

/** Resolves once the download has settled, whether or not it defined `Tally`. */
export function loadTally(): Promise<void> {
  tallyLoading ??= injectTallyScript()
  return tallyLoading
}

export async function openTallyPopup(formId: string, options?: TallyPopupOptions): Promise<void> {
  await waitForTally()

  const tally = window.Tally
  if (!tally) {
    getPostHogInstance()?.capture('tally_form_unavailable', { form_id: formId })
    throw new TemporaryProblemError()
  }

  tally.openPopup(formId, {
    width: 500,
    ...options,
  })
  sendMessage(
    createTrackingMessage({
      event: 'viewed_tally_form',
      form_id: formId,
    }),
  )
}

async function waitForTally(): Promise<void> {
  let timer: ReturnType<typeof setTimeout> | undefined
  const timeout = new Promise<void>((resolve) => {
    timer = setTimeout(resolve, tallyLoadTimeoutMs)
  })
  await Promise.race([loadTally(), timeout])
  clearTimeout(timer)
}

function injectTallyScript(): Promise<void> {
  return new Promise((resolve) => {
    const script = document.createElement('script')
    script.src = tallyEmbedScriptUrl
    script.async = true
    script.addEventListener('load', () => resolve(), { once: true })
    script.addEventListener('error', () => resolve(), { once: true })
    document.head.appendChild(script)
  })
}
